// src/components/AssistantPanel.tsx (FULL — with your 3 requested changes)
//
// ✅ Change 1: sidebar narrower via CSS (done in AssistantPanel.css)
// ✅ Change 2: suggestions not bold (done in CSS: .asst-starter-title font-weight)
// ✅ Change 3: when suggestions show, hide the "New conversation started..." system bubble
//             (implemented below via `hideSystemWelcomeWhenEmpty`)

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./AssistantPanel.css";
import { useAuth } from "../auth/AuthProvider";
import { useToast } from "./Toast";
import ConfirmDialog from "./ConfirmDialog";
import { WS_BASE, API_BASE } from "../config";
import { useTurnWatchdog } from "../hooks/useTurnWatchdog";
import { Approval, HarnessCard, toApproval, toCard } from "./assistant/harness";
import { ApprovalPanel, CardView, harnessStyles } from "./assistant/HarnessCards";

// Async-worker silent-death gap (TODO.md): ws/chat_worker runs for up to 300s
// and STREAMS continuously while alive (delta/tool_start/tool_end/title
// frames). If the worker dies at the platform level (Lambda timeout/OOM/
// throttled Event invoke — retry_attempts=0, no DLQ) after the dispatcher's
// fast start/status frames already arrived, no error frame ever follows and
// the client would spin forever. 90s of total frame-silence is treated as
// death — generous relative to normal delta cadence, but well under the
// worker's own budget so a stuck turn still resolves within one panel visit.
const TURN_WATCHDOG_MS = 90_000;

type Role = "user" | "assistant" | "system";
type Message = { id: string; role: Role; content: string; pending?: boolean; cards?: HarnessCard[] };

type ConversationMeta = {
  id: string;
  title: string;
  updatedAt: number; // ms
  messageCount?: number;
};

type Props = { patientId: string; conversationId?: string };

const uid = () => Math.random().toString(36).slice(2, 9);

function decodeJwtNoVerify(token: string): any | null {
  try {
    const [, payload] = token.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

const storageKey = (therapistId: string, patientId: string) =>
  `tj_convos_${therapistId}_${patientId}`;
const SIDEBAR_OPEN_KEY = "tj_asst_sidebar_open";
const EFFORT_KEY = "tj_asst_effort";

// The server sends short, stable error codes (never internals) — map the
// known ones to friendly i18n messages, fall back to the raw code.
const ERROR_CODE_KEYS: Record<string, string> = {
  internal_error: "assistant.errors.internal",
  assistant_unavailable: "assistant.errors.unavailable",
  response_truncated: "assistant.errors.truncated",
  empty_response: "assistant.errors.empty",
  title_generation_failed: "assistant.errors.title",
  "unauthorized: please reconnect": "assistant.errors.unauthorized",
  "identity mismatch": "assistant.errors.unauthorized",
  "missing id_token for tools": "assistant.errors.unauthorized",
  // Client-detected only — never sent by the server. Synthesized by the
  // turn watchdog when the async worker goes silent (see TURN_WATCHDOG_MS).
  worker_silent: "assistant.errors.silentDeath",
};

// Prompt starters like ChatGPT tiles - keys for i18n
const STARTER_KEYS = [
  { titleKey: "assistant.starters.similarSessions", textKey: "assistant.starters.similarSessionsText", subtitleKey: "assistant.starters.similarSessionsSub" },
  { titleKey: "assistant.starters.listSessions", textKey: "assistant.starters.listSessionsText", subtitleKey: "assistant.starters.listSessionsSub" },
  { titleKey: "assistant.starters.schemaFit", textKey: "assistant.starters.schemaFitText", subtitleKey: "assistant.starters.schemaFitSub" },
  { titleKey: "assistant.starters.cbtPlan", textKey: "assistant.starters.cbtPlanText", subtitleKey: "assistant.starters.cbtPlanSub" },
] as const;

// -------- REST helpers --------
async function apiGet(url: string, token: string) {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}
async function apiDelete(url: string, token: string) {
  const r = await fetch(url, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) throw new Error(`${r.status}`);
  return r.json();
}

const listConversations = (base: string, token: string, patientId: string) =>
  apiGet(
    `${base}/assistant/conversations?patient_id=${encodeURIComponent(patientId)}&limit=50`,
    token
  );

const getConversation = (base: string, token: string, id: string, patientId: string) =>
  apiGet(
    `${base}/assistant/conversations/${encodeURIComponent(id)}?patient_id=${encodeURIComponent(
      patientId
    )}`,
    token
  );

const deleteConversation = (base: string, token: string, id: string) =>
  apiDelete(`${base}/assistant/conversations/${encodeURIComponent(id)}`, token);

const AssistantPanel: React.FC<Props> = ({ patientId, conversationId }) => {
  const { t } = useTranslation();
  const { ensureIdToken, ensureToken } = useAuth() as any;

  // identity
  const [idToken, setIdToken] = useState<string | null>(null);
  const therapistIdRef = useRef<string | null>(null);

  // websocket
  const [wsStatus, setWsStatus] = useState<"idle" | "connecting" | "open" | "closed" | "error">(
    "idle"
  );
  const [lastError, setLastError] = useState<string | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const connectingRef = useRef<boolean>(false);
  const currentAssistIdRef = useRef<string | null>(null);
  const ignoreStreamRef = useRef<boolean>(false);

  // conversations
  const [convos, setConvos] = useState<ConversationMeta[]>([]);
  const [activeConv, setActiveConv] = useState<string>("");
  // Ref that always has the latest activeConv, used inside the long-lived
  // WebSocket onmessage handler to avoid stale-closure bugs where messages
  // would be appended to the wrong conversation after switching.
  const activeConvRef = useRef<string>(activeConv);
  activeConvRef.current = activeConv;
  const [editingConvId, setEditingConvId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState<string>("");

  // messages per conversation (chronological)
  const [messagesByConv, setMessagesByConv] = useState<Record<string, Message[]>>({});
  const activeMessages = messagesByConv[activeConv] || [];

  // composer
  const [input, setInput] = useState("");
  const [isStreaming, setStreaming] = useState(false);
  // Ref mirror for the long-lived WebSocket close handler (stale-closure safe)
  const isStreamingRef = useRef(false);
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);
  const [reasoningEffort, setReasoningEffort] = useState<"low" | "medium" | "high">(() => {
    try {
      const v = localStorage.getItem(EFFORT_KEY);
      if (v === "low" || v === "medium" || v === "high") return v;
    } catch {}
    return "medium";
  });
  useEffect(() => {
    try {
      localStorage.setItem(EFFORT_KEY, reasoningEffort);
    } catch {}
  }, [reasoningEffort]);
  const toast = useToast();
  const [deleteConvoId, setDeleteConvoId] = useState<string | null>(null);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  // Harness approvals: side-effecting tools paused until the therapist answers.
  const [approvalsByConv, setApprovalsByConv] = useState<Record<string, Approval[]>>({});
  const [answering, setAnswering] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // sidebar open/close (persisted)
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(() => {
    try {
      const raw = localStorage.getItem(SIDEBAR_OPEN_KEY);
      return raw === null ? true : raw === "1";
    } catch {
      return true;
    }
  });
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  // keyboard shortcuts: Ctrl/Cmd+B to toggle; Esc to focus composer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMod = e.ctrlKey || e.metaKey;
      if (isMod && e.key.toLowerCase() === "b") {
        e.preventDefault();
        toggleSidebar();
      }
      if (e.key === "Escape") {
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleSidebar]);

  // auto-collapse once on small screens
  useEffect(() => {
    try {
      if (localStorage.getItem(SIDEBAR_OPEN_KEY) !== null) return;
    } catch {}
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
      try {
        localStorage.setItem(SIDEBAR_OPEN_KEY, "0");
      } catch {}
    }
  }, []);

  // scrolling: to bottom
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [activeMessages, isStreaming, activeConv]);

  // warm token + therapist sub — RUN ONCE
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const t = await ensureIdToken();
        if (cancelled || !t) return;
        setIdToken((prev) => (prev === t ? prev : t));
        const claims = decodeJwtNoVerify(t) || {};
        therapistIdRef.current = claims?.sub || therapistIdRef.current;
      } catch {}
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // seed from local
  const seedFromLocal = useCallback(() => {
    const tid = therapistIdRef.current;
    if (!tid || !patientId) return { list: [], initial: "" };
    try {
      const raw = localStorage.getItem(storageKey(tid, patientId));
      let list: ConversationMeta[] = (raw && JSON.parse(raw)) || [];
      if (!list.length) {
        const id = conversationId || `conv_${uid()}`;
        list = [{ id, title: t("assistant.mainConversation"), updatedAt: Date.now() }];
      } else if (conversationId && !list.some((c) => c.id === conversationId)) {
        list = [{ id: conversationId, title: t("assistant.mainConversation"), updatedAt: Date.now() }, ...list];
      }
      return { list, initial: (conversationId || list[0]?.id)! };
    } catch {
      const id = conversationId || `conv_${uid()}`;
      return { list: [{ id, title: t("assistant.mainConversation"), updatedAt: Date.now() }], initial: id };
    }
  }, [patientId, conversationId, t]);

  const saveLocal = useCallback(
    (list: ConversationMeta[]) => {
      const tid = therapistIdRef.current;
      if (!tid || !patientId) return;
      try {
        localStorage.setItem(storageKey(tid, patientId), JSON.stringify(list));
      } catch {}
    },
    [patientId]
  );

  // load server conversations
  useEffect(() => {
    (async () => {
      if (!idToken || !patientId) return;
      try {
        const data = await listConversations(API_BASE, idToken, patientId);
        const serverList: ConversationMeta[] = (data.items || []).map((it: any) => ({
          id: it.conversation_id,
          title: it.title || t("assistant.conversationFallback"),
          updatedAt: Number(it.updated_at || 0) * 1000,
          messageCount: Number(it.message_count || 0),
        }));
        const initial = conversationId || serverList[0]?.id || "";
        if (serverList.length) {
          setConvos(serverList.sort((a, b) => b.updatedAt - a.updatedAt));
          setActiveConv(initial || serverList[0].id);
          saveLocal(serverList);
          return;
        }
      } catch {}
      const { list, initial } = seedFromLocal();
      if (list.length) {
        setConvos(list.sort((a, b) => b.updatedAt - a.updatedAt));
        setActiveConv(initial);
      }
    })();
  }, [idToken, patientId, conversationId, seedFromLocal, saveLocal]);

  // load messages for active conversation
  useEffect(() => {
    (async () => {
      const id = activeConv;
      if (!id || !idToken) return;
      if (messagesByConv[id]) return;
      try {
        const data = await getConversation(API_BASE, idToken, id, patientId);
        const msgsRaw: any[] = data.messages || [];
        msgsRaw.sort((a, b) => Number(a.ts || 0) - Number(b.ts || 0));
        const msgs = msgsRaw.map((m) => ({
          id: uid(),
          role: (m.role as Role) || "assistant",
          content: String(m.content || ""),
          cards: (m.cards || []).map(toCard),
        }));
        setApprovalsByConv((prev) => ({ ...prev, [id]: (data.pending_approvals || []).map(toApproval) }));
        setMessagesByConv((prev) => ({
          ...prev,
          [id]: msgs.length
            ? msgs
            : [
                {
                  id: uid(),
                  role: "system",
                  content: t("assistant.welcome"),
                },
              ],
        }));
      } catch {
        setMessagesByConv((prev) =>
          prev[id]
            ? prev
            : {
                ...prev,
                [id]: [
                  {
                    id: uid(),
                    role: "system",
                    content:
                      "You’re chatting with **Affektra**. Ask for formulations, interventions, homework ideas, or summaries.",
                  },
                ],
              }
        );
      }
    })();
  }, [activeConv, idToken, patientId, messagesByConv, t]);

  useEffect(() => {
    saveLocal(convos);
  }, [convos, saveLocal]);

  // Fires when the turn watchdog concludes the async worker died silently
  // (see TURN_WATCHDOG_MS). Mirrors the existing `msg.type === "error"`
  // handling below so the user sees the same "response interrupted" style
  // error bubble + toast, without assuming the underlying socket is dead —
  // it's the worker invoke that's gone, not necessarily the ws/$connect.
  const handleWorkerSilence = useCallback(() => {
    const conv = activeConvRef.current;
    const id = currentAssistIdRef.current;
    const friendlyKey = ERROR_CODE_KEYS.worker_silent;
    const text = `⚠ ${t(friendlyKey)}`;
    if (id) {
      setMessagesByConv((prev) => ({
        ...prev,
        [conv]: (prev[conv] || []).map((m) =>
          m.id === id ? { ...m, pending: false, content: (m.content || "") + "\n" + text } : m
        ),
      }));
    } else {
      setMessagesByConv((prev) => ({
        ...prev,
        [conv]: [...(prev[conv] || []), { id: uid(), role: "assistant", content: text }],
      }));
    }
    currentAssistIdRef.current = null;
    setStreaming(false);
    setToolStatus(null);
    ignoreStreamRef.current = false;
    toast.error(t(friendlyKey));
  }, [t, toast]);

  const turnWatchdog = useTurnWatchdog(handleWorkerSilence, TURN_WATCHDOG_MS);

  // ---- WebSocket helpers ----
  const hardClose = useCallback(() => {
    try {
      wsRef.current?.close();
    } catch {}
    wsRef.current = null;
    connectingRef.current = false;
    setWsStatus("closed");
    turnWatchdog.clear();
  }, [turnWatchdog]);
  useEffect(() => () => hardClose(), [hardClose]);

  const confirmDeleteConvo = useCallback(async () => {
    if (!deleteConvoId) return;
    try {
      const t = idToken ?? (await ensureIdToken());
      if (!t) throw new Error("No ID token");
      await deleteConversation(API_BASE, t, deleteConvoId);
      setConvos((prev) => prev.filter((x) => x.id !== deleteConvoId));
      setMessagesByConv((prev) => {
        const copy = { ...prev };
        delete copy[deleteConvoId];
        return copy;
      });
      if (activeConv === deleteConvoId) {
        const remaining = convos.filter((x) => x.id !== deleteConvoId);
        const next = remaining[0]?.id || "";
        setActiveConv(next);
      }
    } catch (err) {
      console.error("Delete failed", err);
      toast.error(t("assistant.deleteFailed"));
    } finally {
      setDeleteConvoId(null);
    }
  }, [deleteConvoId, idToken, ensureIdToken, activeConv, convos, toast]);

  const waitForOpen = (sock: WebSocket, timeoutMs = 8000) =>
    new Promise<void>((resolve, reject) => {
      if (sock.readyState === WebSocket.OPEN) return resolve();
      const onOpen = () => {
        cleanup();
        resolve();
      };
      const onError = () => {
        cleanup();
        reject(new Error("socket error"));
      };
      const onClose = () => {
        cleanup();
        reject(new Error("socket closed"));
      };
      const timer = setTimeout(() => {
        cleanup();
        reject(new Error("open timeout"));
      }, timeoutMs);
      function cleanup() {
        clearTimeout(timer);
        sock.removeEventListener("open", onOpen);
        sock.removeEventListener("error", onError);
        sock.removeEventListener("close", onClose);
      }
      sock.addEventListener("open", onOpen);
      sock.addEventListener("error", onError);
      sock.addEventListener("close", onClose);
    });

  const connectOnce = useCallback(
    async () => {
      if (!WS_BASE) {
        setLastError("WS_BASE not configured");
        setWsStatus("error");
        return null;
      }
      let token = idToken;
      if (!token) {
        try {
          const t = await ensureIdToken();
          if (t) {
            token = t;
            setIdToken((prev) => (prev === t ? prev : t));
            const claims = decodeJwtNoVerify(t) || {};
            therapistIdRef.current = claims?.sub || therapistIdRef.current;
          }
        } catch {}
      }
      if (!token) {
        setLastError("No ID token");
        setWsStatus("error");
        return null;
      }
      if (connectingRef.current) return wsRef.current;
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        setWsStatus("open");
        return wsRef.current;
      }

      try {
        connectingRef.current = true;
        setWsStatus("connecting");
        setLastError(null);
        const url = `${WS_BASE}${WS_BASE.includes("?") ? "&" : "?"}token=${encodeURIComponent(
          token
        )}`;
        const sock = new WebSocket(url);
        wsRef.current = sock;

        sock.onopen = () => {
          connectingRef.current = false;
          setWsStatus("open");
        };
        sock.onclose = (ev) => {
          connectingRef.current = false;
          setWsStatus("closed");
          setLastError(`code=${ev.code} reason="${ev.reason || "(none)"}"`);
          // The socket is gone, so no more frames can arrive either way —
          // stop the turn watchdog to avoid a redundant/late silence error.
          turnWatchdog.clear();
          // If the socket dies mid-answer, don't leave the bubble blinking
          // "pending" forever — mark it interrupted and tell the user.
          if (isStreamingRef.current) {
            const conv = activeConvRef.current;
            const id = currentAssistIdRef.current;
            if (id) {
              setMessagesByConv((prev) => ({
                ...prev,
                [conv]: (prev[conv] || []).map((m) =>
                  m.id === id
                    ? {
                        ...m,
                        pending: false,
                        content: (m.content ? m.content + "\n\n" : "") + t("assistant.interrupted"),
                      }
                    : m
                ),
              }));
            }
            currentAssistIdRef.current = null;
            setStreaming(false);
            setToolStatus(null);
            toast.error(t("assistant.connectionLost"));
          }
        };
        sock.onerror = () => {
          setWsStatus("error");
          setLastError("socket error");
        };

        sock.onmessage = (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (!msg || typeof msg !== "object") return;
            if (ignoreStreamRef.current && msg.type !== "error") return;

            // Read current conversation from ref, NOT the closure variable,
            // because this handler lives as long as the WebSocket and the
            // user may have switched conversations since connectOnce ran.
            const conv = activeConvRef.current;

            // Turn watchdog: "done"/"error" end the turn (stop watching);
            // every other in-turn frame — including the dispatcher's own
            // "start"/"status" — proves the worker is alive and (re)starts
            // the silence window. See TURN_WATCHDOG_MS above.
            if (msg.type === "done" || msg.type === "error") {
              turnWatchdog.clear();
            } else if (
              msg.type === "start" ||
              msg.type === "status" ||
              msg.type === "tool_start" ||
              msg.type === "tool_end" ||
              msg.type === "delta" ||
              msg.type === "message" ||
              msg.type === "title" ||
              msg.type === "card" ||
              msg.type === "approval_request"
            ) {
              turnWatchdog.reset();
            } else if (msg.type === "awaiting_approval") {
              // The turn is paused on the therapist, not the worker: stop watching.
              turnWatchdog.clear();
            }

            if (msg.type === "start") {
              setStreaming(true);
              setToolStatus(null);
              const id = uid();
              currentAssistIdRef.current = id;
              setMessagesByConv((prev) => ({
                ...prev,
                [conv]: [
                  ...(prev[conv] || []),
                  { id, role: "assistant", content: "", pending: true },
                ],
              }));
            } else if (msg.type === "status") {
              // Empty status means "clear" (sent right before text starts).
              const s = String(msg.status || "");
              if (!s) setToolStatus(null);
              else setToolStatus(s === "Thinking..." ? t("assistant.thinking") : s);
            } else if (msg.type === "tool_start") {
              const toolName = String(msg.tool || "tool").replace(/_/g, " ");
              setToolStatus(t("assistant.usingTool", { tool: toolName }));
            } else if (msg.type === "tool_end") {
              // Keep status until next status update or done
            } else if (msg.type === "delta") {
              const id = currentAssistIdRef.current;
              if (!id) return;
              const chunk = String(msg.content || "");
              setMessagesByConv((prev) => ({
                ...prev,
                [conv]: (prev[conv] || []).map((m) =>
                  m.id === id ? { ...m, content: m.content + chunk } : m
                ),
              }));
            } else if (msg.type === "message") {
              const text = String(msg.content || "");
              const id = currentAssistIdRef.current || uid();
              if (currentAssistIdRef.current) {
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: (prev[conv] || []).map((m) =>
                    m.id === id ? { ...m, content: text } : m
                  ),
                }));
              } else {
                currentAssistIdRef.current = id;
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: [
                    ...(prev[conv] || []),
                    { id, role: "assistant", content: text, pending: true },
                  ],
                }));
              }
            } else if (msg.type === "card") {
              const card = toCard(msg);
              let id = currentAssistIdRef.current;
              if (!id) {
                id = uid();
                currentAssistIdRef.current = id;
                const newId = id;
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: [...(prev[conv] || []), { id: newId, role: "assistant", content: "", pending: true, cards: [card] }],
                }));
              } else {
                const openId = id;
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: (prev[conv] || []).map((m) =>
                    m.id === openId ? { ...m, cards: [...(m.cards || []), card] } : m
                  ),
                }));
              }
            } else if (msg.type === "approval_request") {
              const approval = toApproval(msg);
              setApprovalsByConv((prev) => ({ ...prev, [conv]: [...(prev[conv] || []), approval] }));
            } else if (msg.type === "awaiting_approval") {
              setToolStatus(null);
            } else if (msg.type === "title") {
              const title = String(msg.title || "");
              if (title) {
                setConvos((prev) =>
                  prev
                    .map((c) => (c.id === conv ? { ...c, title, updatedAt: Date.now() } : c))
                    .sort((a, b) => b.updatedAt - a.updatedAt)
                );
              }
            } else if (msg.type === "done") {
              const id = currentAssistIdRef.current;
              if (id) {
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: (prev[conv] || []).map((m) =>
                    m.id === id ? { ...m, pending: false } : m
                  ),
                }));
              }
              currentAssistIdRef.current = null;
              setStreaming(false);
              setToolStatus(null);
              ignoreStreamRef.current = false;

              setConvos((prev) =>
                prev
                  .map((c) => (c.id === conv ? { ...c, updatedAt: Date.now() } : c))
                  .sort((a, b) => b.updatedAt - a.updatedAt)
              );
            } else if (msg.type === "error") {
              const id = currentAssistIdRef.current;
              const code = String(msg.error || "");
              const friendlyKey = ERROR_CODE_KEYS[code];
              const text = `⚠ ${friendlyKey ? t(friendlyKey) : code || t("assistant.error")}`;
              if (id) {
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: (prev[conv] || []).map((m) =>
                    m.id === id
                      ? { ...m, pending: false, content: (m.content || "") + "\n" + text }
                      : m
                  ),
                }));
              } else {
                setMessagesByConv((prev) => ({
                  ...prev,
                  [conv]: [
                    ...(prev[conv] || []),
                    { id: uid(), role: "assistant", content: text },
                  ],
                }));
              }
              currentAssistIdRef.current = null;
              setStreaming(false);
              setToolStatus(null);
              ignoreStreamRef.current = false;
            } else {
              throw new Error(`Unknown frame type: ${msg.type}`);
            }
          } catch (e) {
            // Protocol drift / bad frame: loud in the console AND visible.
            console.error("[assistant] failed to handle frame", ev.data, e);
            setLastError(`frame error: ${e instanceof Error ? e.message : String(e)}`);
            toast.error(t("assistant.error"));
          }
        };

        return sock;
      } catch (e: any) {
        connectingRef.current = false;
        setWsStatus("error");
        setLastError(e?.message || "connect failed");
        return null;
      }
    },
    [WS_BASE, idToken, ensureIdToken, t, toast, turnWatchdog]
  );

  const addUserMessage = (text: string) => {
    setMessagesByConv((prev) => ({
      ...prev,
      [activeConv]: [...(prev[activeConv] || []), { id: uid(), role: "user", content: text }],
    }));
  };

  // A refused WS handshake is opaque to the browser (JS can't read the
  // handshake response status or body), so a plan-gated 403 from ws/$connect
  // would otherwise look like a generic network error. On connect failure,
  // check the subscription (assistant chat is Pro-gated) and explain properly.
  // Runs only on the error path — the happy path never fetches this.
  const explainConnectFailure = useCallback(async () => {
    try {
      const token = await ensureToken();
      const r = await fetch(`${API_BASE}/payments/subscription`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`${r.status}`);
      const sub = await r.json();
      // ws/connect gates on has_pro (trial | pro) — "none" AND "alap" are refused.
      if (!["trial", "pro"].includes(sub?.effective_plan)) {
        toast.error(t("settings.planRequired"));
        return;
      }
    } catch (e) {
      console.error("[assistant] subscription check after WS connect failure failed", e);
    }
    toast.error(t("assistant.sendFailed"));
  }, [ensureToken, t, toast]);

  const sendChat = useCallback(
    async (text: string) => {
      let s = wsRef.current;
      if (!s || s.readyState !== WebSocket.OPEN) {
        s = await connectOnce();
        if (!s) {
          setLastError("WebSocket not open (connect failed)");
          void explainConnectFailure();
          return;
        }
        try {
          await waitForOpen(s, 8000);
        } catch (e: any) {
          setLastError(`WebSocket open failed: ${e?.message || "timeout"}`);
          void explainConnectFailure();
          return;
        }
      }
      const therapist_id = therapistIdRef.current;
      if (!therapist_id || !patientId || !activeConv) {
        setLastError("Missing therapist_id, patient_id, or conversation_id");
        toast.error(t("assistant.sendFailed"));
        return;
      }
      ignoreStreamRef.current = false;
      // The server keeps the authoritative history and only reads the latest
      // user message — sending the whole thread again would just be payload.
      s.send(
        JSON.stringify({
          action: "chat",
          conversation_id: activeConv,
          patient_id: patientId,
          user_id: therapist_id,
          messages: [{ role: "user", content: text }],
          reasoning_effort: reasoningEffort,
          id_token: idToken, // Pass token for MCP gateway auth
          capabilities: ["approvals"], // harness: cards + approval-gated tools
        })
      );
      setApprovalsByConv((prev) => ({ ...prev, [activeConv]: [] }));
    },
    [connectOnce, explainConnectFailure, patientId, activeConv, idToken, reasoningEffort, t, toast]
  );

  const onSend = async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    setInput("");
    addUserMessage(text);
    await sendChat(text);
  };

  /** Answers every pending approval at once (the server requires all). */
  const onAnswerApprovals = async (approve: boolean) => {
    const pending = approvalsByConv[activeConv] || [];
    if (!pending.length) return;
    const s = wsRef.current;
    if (!s || s.readyState !== WebSocket.OPEN) {
      toast.error(t("assistant.connectionLost"));
      return;
    }
    setAnswering(true);
    try {
      s.send(JSON.stringify({
        action: "approve",
        conversation_id: activeConv,
        patient_id: patientId,
        approvals: pending.map((a) => ({ approval_request_id: a.approval_request_id, approve })),
        id_token: idToken,
      }));
      setApprovalsByConv((prev) => ({ ...prev, [activeConv]: [] }));
      turnWatchdog.reset();
    } catch (e) {
      console.error("[assistant] sending approval failed", e);
      toast.error(t("assistant.sendFailed"));
    } finally {
      setAnswering(false);
    }
  };

  const onStop = () => {
    if (!isStreaming) return;
    ignoreStreamRef.current = true;
    setStreaming(false);
    // User-initiated cancel — no more frames are expected for this turn,
    // so stop watching for silence (avoid a spurious later timeout error).
    turnWatchdog.clear();
    const cur = currentAssistIdRef.current;
    if (cur) {
      setMessagesByConv((prev) => ({
        ...prev,
        [activeConv]: prev[activeConv].map((m) => (m.id === cur ? { ...m, pending: false } : m)),
      }));
    }
    try {
      wsRef.current?.send(JSON.stringify({ action: "cancel" }));
    } catch {}
  };

  const disabled = isStreaming || !input.trim();
  const sidebarId = "asst-sidebar";

  // Starter click: put text into input box (no auto-send)
  const onStarterClick = useCallback((text: string) => {
    setInput(text);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = text.length;
      try {
        el.setSelectionRange(len, len);
      } catch {}
    });
  }, []);

  // Decide when to show the empty-state starters
  const isOnlySystemWelcome = (() => {
    const msgs = activeMessages || [];
    const nonPending = msgs.filter((m) => !m.pending);
    const hasUserOrAssistant = nonPending.some((m) => m.role === "user" || m.role === "assistant");
    return !hasUserOrAssistant;
  })();

  // ✅ when starters are visible, hide the initial "New conversation started..." system bubble
  const hideSystemWelcomeWhenEmpty = isOnlySystemWelcome;

  return (
    <>
    <div className={`asst-shell ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
      {/* LEFT SIDEBAR */}
      <aside
        id={sidebarId}
        className={`asst-sidebar ${sidebarOpen ? "" : "is-collapsed"}`}
        aria-hidden={!sidebarOpen}
      >
        <div className="asst-sidebar-header">
          <button
            className="asst-primary"
            onClick={() => {
              const newId = `conv_${uid()}`;
              const conv: ConversationMeta = {
                id: newId,
                title: t("assistant.newConversation"),
                updatedAt: Date.now(),
              };
              setConvos((prev) => [conv, ...prev]);

              // keep seeding system message (fine), we just hide it when starters are shown
              setMessagesByConv((prev) => ({
                ...prev,
                [newId]: [
                  {
                    id: uid(),
                    role: "system",
                    content: t("assistant.newConversationStarted"),
                  },
                ],
              }));

              setActiveConv(newId);
            }}
          >
            <span className="asst-primary__icon">＋</span>
            <span>{t("assistant.newChat")}</span>
          </button>
        </div>

        <div className="asst-convo-list">
          {convos.map((c) => {
            const isActive = c.id === activeConv;
            const isEditing = editingConvId === c.id;
            return (
              <div
                key={c.id}
                className={`asst-convo-item ${isActive ? "is-active" : ""}`}
                onClick={() => !isEditing && setActiveConv(c.id)}
                title={c.id}
              >
                <div className="asst-convo-row">
                  {isEditing ? (
                    <input
                      type="text"
                      className="asst-convo-edit-input"
                      value={editingTitle}
                      onChange={(e) => setEditingTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setConvos((prev) =>
                            prev
                              .map((x) => (x.id === c.id ? { ...x, title: editingTitle || c.title, updatedAt: Date.now() } : x))
                              .sort((a, b) => b.updatedAt - a.updatedAt)
                          );
                          setEditingConvId(null);
                        } else if (e.key === "Escape") {
                          setEditingConvId(null);
                        }
                      }}
                      onBlur={() => {
                        setConvos((prev) =>
                          prev
                            .map((x) => (x.id === c.id ? { ...x, title: editingTitle || c.title, updatedAt: Date.now() } : x))
                            .sort((a, b) => b.updatedAt - a.updatedAt)
                        );
                        setEditingConvId(null);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                  ) : (
                    <div
                      className="asst-convo-title"
                      onClick={(e) => {
                        if (isActive) {
                          e.stopPropagation();
                          setEditingConvId(c.id);
                          setEditingTitle(c.title);
                        }
                      }}
                      style={{ cursor: isActive ? "text" : "pointer" }}
                    >
                      {c.title}
                    </div>
                  )}
                  {isActive && !isEditing && (
                    <button
                      className="asst-icon-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConvoId(c.id);
                      }}
                      aria-label={t("common.delete")}
                      title={t("assistant.deleteConversation")}
                    >
                      <Trash2 size={12} color="var(--color-danger)" />
                    </button>
                  )}
                </div>
                <div className="asst-convo-sub">
                  {new Date(c.updatedAt).toLocaleString()}
                </div>
              </div>
            );
          })}
        </div>

        <div className="asst-sidebar-footer">
          {lastError && (
            <span className="asst-chip asst-chip--err" title={lastError}>
              err
            </span>
          )}
        </div>
      </aside>

      {!sidebarOpen && (
        <button
          className="asst-sidebar-tab"
          aria-label={t("assistant.showChatHistory")}
          title={t("assistant.showChatHistoryHint")}
          onClick={toggleSidebar}
          aria-controls={sidebarId}
          aria-expanded={sidebarOpen}
        >
          ❯
        </button>
      )}

      {/* RIGHT CHAT PANE */}
      <main className="asst-root">
        <div className="asst-effort">
          <span className="asst-effort-hint">{t("assistant.effort.hint")}:</span>
          <span className="asst-effort-label">{t("assistant.effort.quick")}</span>
          <input
            type="range"
            min={0}
            max={2}
            step={1}
            value={reasoningEffort === "low" ? 0 : reasoningEffort === "medium" ? 1 : 2}
            onChange={(e) => {
              const v = Number(e.target.value);
              setReasoningEffort(v === 0 ? "low" : v === 1 ? "medium" : "high");
            }}
            className="asst-effort-slider"
            title={t(`assistant.effort.${reasoningEffort === "low" ? "quick" : reasoningEffort === "medium" ? "balanced" : "deep"}`)}
          />
          <span className="asst-effort-label">{t("assistant.effort.deep")}</span>
        </div>

        <div className="asst-scroll">
          {/* messages */}
          {activeMessages
            .filter((m) => !(hideSystemWelcomeWhenEmpty && m.role === "system"))
            .map((m) => (
              <div key={m.id} className={`asst-row asst-row--${m.role}`}>
                <div className="asst-bubble">
                  <div className="asst-text">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" />
                        ),
                      }}
                    >
                      {m.content}
                    </ReactMarkdown>
                  </div>
                  {(m.cards || []).map((c, i) => <CardView key={i} card={c} />)}
                  {m.pending && <span className="asst-cursor" />}
                </div>
              </div>
            ))}

          {/* Tool/thinking status */}
          {toolStatus && (
            <div className="asst-status">
              <span className="asst-status-dot" />
              {toolStatus}
            </div>
          )}

          {/* starters (empty state) */}
          {isOnlySystemWelcome && (
            <div className="asst-empty">
              <div className="asst-empty-inner" aria-label="Conversation starters">
                <div className="asst-starters">
                  {STARTER_KEYS.map((s) => (
                    <button
                      key={s.titleKey}
                      type="button"
                      className="asst-starter"
                      onClick={() => onStarterClick(t(s.textKey))}
                      disabled={isStreaming}
                      title={t(s.textKey)}
                    >
                      <div className="asst-starter-title">{t(s.titleKey)}</div>
                      {s.subtitleKey && <div className="asst-starter-sub">{t(s.subtitleKey)}</div>}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div ref={endRef} />
        </div>

        <ApprovalPanel approvals={approvalsByConv[activeConv] || []} busy={answering} onAnswer={onAnswerApprovals} />
        <style>{harnessStyles}</style>

        <div className="asst-composer">
          <textarea
            ref={inputRef}
            className="asst-input"
            placeholder={t("assistant.placeholder")}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void onSend();
              }
            }}
          />
          <div className="asst-actions">
            <button className="asst-send" onClick={onSend} disabled={disabled}>
              {isStreaming ? <span className="asst-spinner" /> : t("assistant.send")}
            </button>
          </div>
        </div>
      </main>
    </div>
    <ConfirmDialog
      open={!!deleteConvoId}
      title={t("assistant.deleteConversation")}
      message={t("assistant.deleteConversationMessage")}
      confirmLabel={t("common.delete")}
      variant="danger"
      onConfirm={confirmDeleteConvo}
      onCancel={() => setDeleteConvoId(null)}
    />
    </>
  );
};

export default AssistantPanel;
